import React, { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Menu, X, Info, Users, Home, Landmark, Calculator, Star, AlertCircle } from "lucide-react";
import { BIZ, SERVICES, DISCIPLINES, TEAM, HOW_WE_WORK, REPUTATION } from "./gutierrezData";
import ConceptCTAPill from "@/components/ConceptCTAPill";
import LawInquiryForm from "@/components/LawInquiryForm";

const INK = "#221F1F";
const RED = "#DC2626";
const CREAM = "#FAF6EE";
const TAN = "#EFE4D0";
const SLATE = "#5C554C";

const fadeUp = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" }, transition: { duration: 0.5, ease: "easeOut" } };

const DISCIPLINE_ICON = { Legal: Landmark, Accounting: Calculator, "Real Estate": Home };

export default function GutierrezLegalTeamSite() {
  const [open, setOpen] = useState(false);
  const [activeDiscipline, setActiveDiscipline] = useState("Legal");
  const tagalog = typeof window !== "undefined" && window.localStorage.getItem("mysitecare-language") === "tl";
  const links = [["What We Do", "#services"], ["Our Team", "#team"], ["How We Work", "#how"], ["Contact", "#contact"]];

  return (
    <div id="top" className="min-h-screen" style={{ background: CREAM, color: INK, fontFamily: "'Inter', sans-serif" }} data-testid="gutierrez-concept-root">
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Lora:wght@500;600;700&family=Inter:wght@400;500;600;700&display=swap'); .glt-serif{font-family:'Lora',serif;}`}</style>

      <div data-testid="law-disclaimer" className="bg-black text-white text-center text-xs py-2.5 px-4">
        <span className="inline-flex items-center gap-2"><Info size={13} style={{ color: RED }} />Concept website created by <a href="https://mysitecare.eu/ph" target="_blank" rel="noopener noreferrer" className="underline decoration-white/40 hover:decoration-white">MySiteCare</a> — not currently operated by or affiliated with {BIZ.name}.</span>
      </div>

      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur shadow-sm">
        <nav className="max-w-6xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <a href="#top" data-testid="gutierrez-logo" className="flex items-center gap-3">
            <img src="/law/gutierrez-legal-team-concept/logo-mark.jpg" alt="The Gutierrez Legal Team logo" className="h-9 w-9 object-contain" />
            <span className="glt-serif text-lg" style={{ color: INK }}>The Gutierrez Legal Team</span>
          </a>
          <div className="hidden lg:flex items-center gap-8">
            {links.map(([l, h]) => <a key={h} href={h} className="text-sm font-medium text-[#5C554C] hover:text-[#221F1F] transition-colors">{l}</a>)}
          </div>
          <a href="#contact" data-testid="gutierrez-header-cta" className="hidden sm:inline-flex rounded-full px-6 py-2.5 text-sm font-semibold text-white" style={{ background: RED }}>Get in Touch</a>
          <button className="lg:hidden" onClick={() => setOpen(!open)} data-testid="gutierrez-menu-toggle" aria-label="Menu">{open ? <X size={24} /> : <Menu size={24} />}</button>
        </nav>
        {open && <div className="lg:hidden px-5 pb-4 space-y-1 bg-white">{links.map(([l, h]) => <a key={h} href={h} onClick={() => setOpen(false)} className="block py-2.5 font-medium text-[#221F1F]">{l}</a>)}</div>}
      </header>

      {/* HERO — three vertical bands ARE the differentiator, not a separate section later */}
      <section className="grid sm:grid-cols-3" data-testid="gutierrez-hero">
        {DISCIPLINES.map((d, i) => {
          const Icon = DISCIPLINE_ICON[d];
          return (
            <div key={d} className="px-6 py-14 sm:py-20 flex flex-col items-center text-center" style={{ background: i === 1 ? INK : TAN }}>
              <Icon size={26} style={{ color: i === 1 ? RED : INK }} />
              <p className="mt-3 text-xs font-bold uppercase tracking-[0.2em]" style={{ color: i === 1 ? "white" : INK }}>{d}</p>
            </div>
          );
        })}
        <div className="sm:col-span-3 py-14 text-center px-5" style={{ background: CREAM }}>
          <motion.img {...fadeUp} src="/law/gutierrez-legal-team-concept/logo-mark.jpg" alt="" className="h-14 w-14 object-contain mx-auto mb-5" />
          <motion.h1 {...fadeUp} className="glt-serif text-3xl sm:text-5xl leading-[1.15] max-w-2xl mx-auto" style={{ color: INK }}>{BIZ.tagline}</motion.h1>
          <motion.p {...fadeUp} className="mt-5 text-base max-w-xl mx-auto" style={{ color: SLATE }}>A family of lawyers, accountants and real-estate brokers, working as one coordinated team.</motion.p>
          <motion.div {...fadeUp} className="mt-8 flex flex-wrap justify-center gap-4">
            <a href="#contact" data-testid="gutierrez-hero-cta" className="rounded-full px-7 py-3.5 text-sm font-semibold text-white" style={{ background: RED }}>Request Consultation</a>
            <a href={`tel:${BIZ.phone}`} data-testid="gutierrez-hero-call" className="rounded-full px-7 py-3.5 text-sm font-semibold border" style={{ borderColor: INK, color: INK }}>Call {BIZ.phoneDisplay}</a>
          </motion.div>
        </div>
      </section>

      {/* WHAT WE DO — tab switcher across the three disciplines, not a static grid */}
      <section id="services" className="py-16">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <motion.h2 {...fadeUp} className="glt-serif text-3xl text-center mb-4" style={{ color: INK }}>What We Do</motion.h2>
          <motion.p {...fadeUp} className="text-center text-sm max-w-xl mx-auto mb-10" style={{ color: SLATE }}>One team, three perspectives. Choose a discipline to see what it covers.</motion.p>
          <div className="flex justify-center gap-2 mb-10">
            {DISCIPLINES.map((d) => (
              <button
                key={d}
                onClick={() => setActiveDiscipline(d)}
                data-testid={`gutierrez-tab-${d.toLowerCase().replace(/\s/g, "-")}`}
                className="rounded-full px-5 py-2.5 text-sm font-semibold transition-colors"
                style={activeDiscipline === d ? { background: RED, color: "white" } : { background: TAN, color: INK }}
              >
                {d}
              </button>
            ))}
          </div>
          <div className="grid sm:grid-cols-3 gap-5">
            {SERVICES.filter((s) => s.discipline === activeDiscipline).map((s) => (
              <motion.div key={s.title} {...fadeUp} className="p-6 bg-white rounded-lg shadow-sm border-t-4" style={{ borderColor: RED }}>
                <h3 className="glt-serif text-base" style={{ color: INK }}>{s.title}</h3>
                <p className="mt-1.5 text-sm" style={{ color: SLATE }}>{s.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TEAM — signature-block style, not cards */}
      <section id="team" className="py-16" style={{ background: INK }}>
        <div className="max-w-2xl mx-auto px-5 sm:px-8 text-center">
          <motion.h2 {...fadeUp} className="glt-serif text-2xl sm:text-3xl text-white mb-10 flex items-center justify-center gap-2"><Users size={22} style={{ color: RED }} /> Our Team</motion.h2>
          <div className="divide-y divide-white/10">
            {TEAM.map((t) => (
              <motion.div key={t.name} {...fadeUp} className="py-5">
                <p className="glt-serif text-xl text-white">{t.name}</p>
                <p className="mt-1 text-sm text-white/60">{t.role}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW WE WORK — inline flowing steps, not circles */}
      <section id="how" className="py-16" style={{ background: TAN }}>
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <motion.h2 {...fadeUp} className="glt-serif text-3xl text-center mb-10" style={{ color: INK }}>How We Work</motion.h2>
          <div className="space-y-6">
            {HOW_WE_WORK.map((s) => (
              <motion.div key={s.n} {...fadeUp} className="flex gap-4">
                <span className="glt-serif text-3xl shrink-0" style={{ color: RED }}>{s.n}</span>
                <p className="text-sm pt-1" style={{ color: SLATE }}><strong style={{ color: INK }}>{s.title}.</strong> {s.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* REPUTATION */}
      <section className="py-14">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 text-center">
          <motion.div {...fadeUp} className="inline-flex items-center gap-1.5 mb-3">
            {[...Array(5)].map((_, i) => <Star key={i} size={18} fill={RED} style={{ color: RED }} />)}
          </motion.div>
          <motion.p {...fadeUp} className="glt-serif text-2xl" style={{ color: INK }}>{tagalog ? `${REPUTATION.rating}★ sa ${REPUTATION.reviewCountLabel}` : `${REPUTATION.rating}★ across ${REPUTATION.reviewCountLabel}`}</motion.p>
          <motion.p {...fadeUp} className="mt-2 text-xs" style={{ color: SLATE }}>{REPUTATION.note}</motion.p>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="py-16" style={{ background: TAN }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-10">
          <motion.div {...fadeUp}>
            <h2 className="glt-serif text-3xl mb-6" style={{ color: INK }}>Get in Touch</h2>
            <ul className="space-y-4 text-sm mb-6">
              <li className="flex items-start gap-3"><MapPin size={18} style={{ color: RED }} className="mt-0.5 shrink-0" />{BIZ.address}</li>
              <li className="flex items-start gap-3"><Phone size={18} style={{ color: RED }} className="mt-0.5 shrink-0" /><a href={`tel:${BIZ.phone}`} data-testid="gutierrez-phone-link" className="hover:underline">{BIZ.phoneDisplay}</a></li>
              <li className="flex items-start gap-3"><Mail size={18} style={{ color: RED }} className="mt-0.5 shrink-0" /><a href={`mailto:${BIZ.email}`} data-testid="gutierrez-email-link" className="hover:underline">{BIZ.email}</a></li>
            </ul>
            <div className="flex items-start gap-2.5 rounded-lg p-4 text-xs mb-6" style={{ background: "rgba(0,0,0,0.04)", color: SLATE }}>
              <AlertCircle size={16} className="shrink-0 mt-0.5" style={{ color: RED }} />
              {BIZ.addressNote}
            </div>
            <div className="rounded-lg overflow-hidden shadow-sm min-h-[220px]">
              <iframe title="Gutierrez Legal Team map" src={BIZ.mapEmbed} className="w-full h-full min-h-[220px]" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
            </div>
          </motion.div>
          <motion.div {...fadeUp}>
            <LawInquiryForm
              testidPrefix="gutierrez"
              businessName={BIZ.name}
              accent={RED}
              accentText="#FFFFFF"
              areasOfLaw={["Estate Planning", "Property Transfer", "Corporate", "Family", "Tax & Estate Coordination", "Other / Not sure"]}
              phone={BIZ.phone}
              phoneDisplay={BIZ.phoneDisplay}
              email={BIZ.email}
            />
          </motion.div>
        </div>
      </section>

      <footer className="py-8 bg-white">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 text-xs" style={{ color: SLATE }}>
          <img src="/law/gutierrez-legal-team-concept/logo-wordmark.jpg" alt="The Gutierrez Legal Team" className="h-7 w-auto object-contain" />
          <p>This is an unsolicited concept website by MySiteCare. Not operated by or affiliated with {BIZ.name}. Confirm current details before visiting.</p>
        </div>
      </footer>

      <ConceptCTAPill slug="gutierrez-legal-team-concept" />
    </div>
  );
}
